'use client';

import { Alert, Button, Input } from 'antd';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { LockOutlined, MailOutlined } from '@ant-design/icons';
import authApi from '@apis/auth';
import { If } from '@components/condition';
import { useCookieAuth } from '@hooks/useCookieAuth';
import { userActions } from '@store/actions';

// Validation schema
const loginSchema = Yup.object().shape({
  email: Yup.string().email('Email không hợp lệ').required('Email là bắt buộc'),
  password: Yup.string()
    .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
    .required('Mật khẩu là bắt buộc'),
});

interface LoginFormProps {
  onForgotPassword: () => void;
  onClose: () => void;
}

export function LoginForm({ onForgotPassword, onClose }: LoginFormProps) {
  const [error, setError] = useState(null);
  const { setToken } = useCookieAuth();
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,

    onSubmit: async (values) => {
      try {
        setError('');
        const res = await authApi.login({
          email: values.email?.trim(),
          password: values.password?.trim(),
        });
        setToken(res.data.accessToken, 'access_token');
        setToken(res.data.refreshToken, 'refresh_token');
        dispatch(userActions.self.getUserInfoSuccess(res?.data?.user));
        onClose();
      } catch (error) {
        setError(error?.message);
      }
    },
  });

  return (
    <div className="py-2">
      <form onSubmit={formik.handleSubmit} autoComplete="off" className="grid gap-4">
        <If
          condition={error}
          Then={
            <Alert
              className="text-sm border-dashed py-3 mb-4"
              message={error}
              type="error"
              showIcon
            />
          }
        />
        <div>
          <div className="text-sm font-medium mb-1">Email</div>
          <Input
            id="email"
            name="email"
            className="!h-12"
            prefix={<MailOutlined />}
            placeholder="your@email.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            status={formik.touched.email && formik.errors.email ? 'error' : ''}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium">Mật khẩu</span>
            <Button type="link" onClick={onForgotPassword} className="p-0 !text-sm">
              Quên mật khẩu?
            </Button>
          </div>
          <Input.Password
            id="password"
            name="password"
            className="!h-12"
            placeholder="Nhập mật khẩu"
            prefix={<LockOutlined />}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            status={formik.touched.password && formik.errors.password ? 'error' : ''}
          />
          {formik.touched.password && formik.errors.password && (
            <div className="text-red-500 text-sm mt-1">{formik.errors.password}</div>
          )}
        </div>

        <div className="!mt-6">
          <Button
            type="primary"
            htmlType="submit"
            className="!h-12"
            loading={formik.isSubmitting}
            block
          >
            {formik.isSubmitting ? 'Đang xử lý...' : 'Đăng nhập'}
          </Button>
        </div>
      </form>

      {/* <Divider plain>Hoặc tiếp tục với</Divider> */}

      {/* <div className="grid grid-cols-2 gap-4">
        <Button icon={<GoogleOutlined />} onClick={handleGoogleLogin} block>
          Google
        </Button>
        <Button icon={<FacebookOutlined />} onClick={handleFacebookLogin} block>
          Facebook
        </Button>
      </div> */}
    </div>
  );
}
